import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, ChevronDownIcon, DownloadIcon, CheckIcon, XIcon, RefreshCwIcon, CheckCircleIcon, CalendarIcon, DollarSignIcon, CreditCardIcon, FileTextIcon, ClockIcon, UserIcon, HashIcon, FileIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock merchant data
const merchantsData = [{
  id: 1,
  merchantId: 'MERCH001',
  name: 'ABC Corporation',
  businessType: 'Retail',
  status: 'active',
  region: 'North',
  storeLocation: 'Main Street, Cityville',
  pendingRTPRequests: 3,
  onboardingDate: '2023-05-15',
  lastRTPGenerated: '2023-10-25'
}, {
  id: 2,
  merchantId: 'MERCH002',
  name: 'XYZ Enterprises',
  businessType: 'E-commerce',
  status: 'active',
  region: 'South',
  storeLocation: 'Online',
  pendingRTPRequests: 0,
  onboardingDate: '2023-06-22',
  lastRTPGenerated: '2023-10-28'
}, {
  id: 3,
  merchantId: 'MERCH003',
  name: 'Local Grocers',
  businessType: 'Grocery',
  status: 'inactive',
  region: 'East',
  storeLocation: 'Oak Avenue, Townsville',
  pendingRTPRequests: 1,
  onboardingDate: '2023-04-10',
  lastRTPGenerated: '2023-09-15'
}, {
  id: 4,
  merchantId: 'MERCH004',
  name: 'Tech Solutions Inc',
  businessType: 'Technology',
  status: 'pending',
  region: 'West',
  storeLocation: 'Tech Park, Innovation City',
  pendingRTPRequests: 0,
  onboardingDate: '2023-07-05',
  lastRTPGenerated: null
}, {
  id: 5,
  merchantId: 'MERCH005',
  name: 'Metro Retailers',
  businessType: 'Retail',
  status: 'active',
  region: 'Central',
  storeLocation: 'Shopping Mall, Downtown',
  pendingRTPRequests: 5,
  onboardingDate: '2023-03-18',
  lastRTPGenerated: '2023-10-10'
}, {
  id: 6,
  merchantId: 'MERCH006',
  name: 'Coffee House Chain',
  businessType: 'Food & Beverage',
  status: 'active',
  region: 'North',
  storeLocation: 'Multiple Locations',
  pendingRTPRequests: 2,
  onboardingDate: '2023-02-14',
  lastRTPGenerated: '2023-10-20'
}, {
  id: 7,
  merchantId: 'MERCH007',
  name: 'Fashion Boutique',
  businessType: 'Retail',
  status: 'active',
  region: 'West',
  storeLocation: 'Fashion Street, Style District',
  pendingRTPRequests: 0,
  onboardingDate: '2023-01-30',
  lastRTPGenerated: '2023-10-15'
}];
interface Merchant {
  id: number;
  merchantId: string;
  name: string;
  businessType: string;
  status: string;
  region: string;
  storeLocation: string;
  pendingRTPRequests: number;
  onboardingDate: string;
  lastRTPGenerated: string | null;
}
interface RTPParameters {
  customerAlias: string;
  customerIBAN: string;
  amount: string;
  currency: string;
  dueDate: string;
  invoiceNumber: string;
  description: string;
  paymentMethod: string;
}
interface GeneratedRTP {
  merchantId: string;
  merchantName: string;
  status: 'pending' | 'generated' | 'failed';
  rtpReference?: string;
  errorMessage?: string;
}
const BulkRTP: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>(merchantsData);
  const [selectedMerchants, setSelectedMerchants] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [pendingFilter, setPendingFilter] = useState('');
  // RTP parameters
  const [rtpParams, setRtpParams] = useState<RTPParameters>({
    customerAlias: '',
    customerIBAN: '',
    amount: '',
    currency: 'PKR',
    dueDate: '',
    invoiceNumber: '',
    description: '',
    paymentMethod: 'rtp'
  });
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedRTPs, setGeneratedRTPs] = useState<GeneratedRTP[]>([]);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // Mock history data
  const [bulkHistory, setBulkHistory] = useState([{
    id: 1,
    date: '2023-10-28T14:30:00Z',
    user: 'admin@example.com',
    merchantCount: 5,
    status: 'completed',
    downloadAvailable: true
  }, {
    id: 2,
    date: '2023-10-25T11:15:00Z',
    user: 'manager@example.com',
    merchantCount: 3,
    status: 'completed',
    downloadAvailable: true
  }, {
    id: 3,
    date: '2023-10-20T09:45:00Z',
    user: 'admin@example.com',
    merchantCount: 7,
    status: 'completed',
    downloadAvailable: true
  }]);
  // Filter merchants based on search and filters
  useEffect(() => {
    let filtered = merchants;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(merchant => merchant.name.toLowerCase().includes(term) || merchant.merchantId.toLowerCase().includes(term) || merchant.storeLocation.toLowerCase().includes(term));
    }
    if (regionFilter) {
      filtered = filtered.filter(merchant => merchant.region === regionFilter);
    }
    if (statusFilter) {
      filtered = filtered.filter(merchant => merchant.status === statusFilter);
    }
    if (businessTypeFilter) {
      filtered = filtered.filter(merchant => merchant.businessType === businessTypeFilter);
    }
    if (pendingFilter) {
      if (pendingFilter === 'hasPending') {
        filtered = filtered.filter(merchant => merchant.pendingRTPRequests > 0);
      } else if (pendingFilter === 'noPending') {
        filtered = filtered.filter(merchant => merchant.pendingRTPRequests === 0);
      }
    }
    setFilteredMerchants(filtered);
  }, [merchants, searchTerm, regionFilter, statusFilter, businessTypeFilter, pendingFilter]);
  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedMerchants([]);
    } else {
      setSelectedMerchants(filteredMerchants.map(merchant => merchant.id));
    }
    setSelectAll(!selectAll);
  };
  // Handle individual merchant selection
  const handleSelectMerchant = (merchantId: number) => {
    if (selectedMerchants.includes(merchantId)) {
      setSelectedMerchants(selectedMerchants.filter(id => id !== merchantId));
      setSelectAll(false);
    } else {
      setSelectedMerchants([...selectedMerchants, merchantId]);
      if (selectedMerchants.length + 1 === filteredMerchants.length) {
        setSelectAll(true);
      }
    }
  };
  // Handle RTP parameters change
  const handleRTPParamChange = (param: keyof RTPParameters, value: string) => {
    setRtpParams({
      ...rtpParams,
      [param]: value
    });
  };
  // Generate RTP requests
  const handleGenerateRTPs = () => {
    if (selectedMerchants.length === 0) {
      alert('Please select at least one merchant');
      return;
    }
    if (!rtpParams.amount || !rtpParams.customerAlias && !rtpParams.customerIBAN) {
      alert('Please enter the required RTP parameters (amount and customer identifier)');
      return;
    }
    setIsGenerating(true);
    setGenerationProgress(0);
    setShowGenerationModal(true);
    // Initialize generated RTPs
    const initialRTPs = selectedMerchants.map(id => {
      const merchant = merchants.find(m => m.id === id)!;
      return {
        merchantId: merchant.merchantId,
        merchantName: merchant.name,
        status: 'pending' as const
      };
    });
    setGeneratedRTPs(initialRTPs);
    // Simulate RTP generation with progress
    let progress = 0;
    const totalMerchants = selectedMerchants.length;
    const interval = setInterval(() => {
      progress += 1;
      setGenerationProgress(Math.floor(progress / totalMerchants * 100));
      // Update status of current merchant
      if (progress <= totalMerchants) {
        setGeneratedRTPs(prev => {
          const updated = [...prev];
          const randomSuccess = Math.random() > 0.1; // 90% success rate
          updated[progress - 1] = {
            ...updated[progress - 1],
            status: randomSuccess ? 'generated' : 'failed',
            rtpReference: randomSuccess ? `RTP${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}` : undefined,
            errorMessage: randomSuccess ? undefined : 'Failed to generate RTP request'
          };
          return updated;
        });
      }
      if (progress >= totalMerchants) {
        clearInterval(interval);
        setIsGenerating(false);
      }
    }, 500);
  };
  // Format date string
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-1981" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-1982" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-1983" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-1984" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-1985" />;
    }
  };
  // Download RTP list
  const handleDownloadRTPs = (format: 'csv' | 'xlsx') => {
    // In a real app, this would trigger a download
    alert(`Downloading RTP list in ${format.toUpperCase()} format`);
    setShowDownloadModal(false);
  };
  return <div className="space-y-6" data-id="element-1986">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1987">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1988">
          Bulk RTP Generation
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-1989">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1990">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-1991" />
            Filters
          </button>
          <button onClick={() => setShowHistoryModal(true)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1992">
            <FileTextIcon className="h-4 w-4 mr-2" data-id="element-1993" />
            Generation History
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-1994">
        <div className="relative flex-1" data-id="element-1995">
          <input type="text" placeholder="Search merchants by name, ID, or location..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-1996" />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1997">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-1998" />
          </div>
        </div>
      </div>

      {showFilters && <Card data-id="element-1999">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2000">
            <div data-id="element-2001">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2002">
                Region
              </label>
              <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2003">
                <option value="" data-id="element-2004">All Regions</option>
                <option value="North" data-id="element-2005">North</option>
                <option value="South" data-id="element-2006">South</option>
                <option value="East" data-id="element-2007">East</option>
                <option value="West" data-id="element-2008">West</option>
                <option value="Central" data-id="element-2009">Central</option>
              </select>
            </div>
            <div data-id="element-2010">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2011">
                Status
              </label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2012">
                <option value="" data-id="element-2013">All Statuses</option>
                <option value="active" data-id="element-2014">Active</option>
                <option value="inactive" data-id="element-2015">Inactive</option>
                <option value="pending" data-id="element-2016">Pending</option>
              </select>
            </div>
            <div data-id="element-2017">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2018">
                Business Type
              </label>
              <select value={businessTypeFilter} onChange={e => setBusinessTypeFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2019">
                <option value="" data-id="element-2020">All Types</option>
                <option value="Retail" data-id="element-2021">Retail</option>
                <option value="E-commerce" data-id="element-2022">E-commerce</option>
                <option value="Grocery" data-id="element-2023">Grocery</option>
                <option value="Technology" data-id="element-2024">Technology</option>
                <option value="Food & Beverage" data-id="element-2025">Food & Beverage</option>
              </select>
            </div>
            <div data-id="element-2026">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2027">
                Pending RTP Requests
              </label>
              <select value={pendingFilter} onChange={e => setPendingFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2028">
                <option value="" data-id="element-2029">All Merchants</option>
                <option value="hasPending" data-id="element-2030">Has Pending Requests</option>
                <option value="noPending" data-id="element-2031">No Pending Requests</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-2032">
            <button onClick={() => {
          setRegionFilter('');
          setStatusFilter('');
          setBusinessTypeFilter('');
          setPendingFilter('');
          setSearchTerm('');
        }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2033">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2034">
              Apply Filters
            </button>
          </div>
        </Card>}

      {/* RTP Parameters Card */}
      <Card data-id="element-2035">
        <h2 className="text-lg font-medium mb-4" data-id="element-2036">Request to Pay Parameters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2037">
          <div data-id="element-2038">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2039">
              Customer Alias <span className="text-red-500" data-id="element-2040">*</span>
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-2041">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2042">
                <UserIcon className="h-5 w-5 text-gray-400" data-id="element-2043" />
              </div>
              <input type="text" placeholder="Customer Alias or Phone Number" value={rtpParams.customerAlias} onChange={e => handleRTPParamChange('customerAlias', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2044" />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2045">
              Enter customer alias or phone number
            </p>
          </div>
          <div data-id="element-2046">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2047">
              Customer IBAN
            </label>
            <input type="text" placeholder="IBAN Number" value={rtpParams.customerIBAN} onChange={e => handleRTPParamChange('customerIBAN', e.target.value)} className="w-full pl-3 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2048" />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2049">
              Either Customer Alias or IBAN is required
            </p>
          </div>
          <div data-id="element-2050">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2051">
              Amount <span className="text-red-500" data-id="element-2052">*</span>
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-2053">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2054">
                <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-2055" />
              </div>
              <input type="text" placeholder="Amount" value={rtpParams.amount} onChange={e => handleRTPParamChange('amount', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2056" />
            </div>
          </div>
          <div data-id="element-2057">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2058">
              Currency
            </label>
            <select value={rtpParams.currency} onChange={e => handleRTPParamChange('currency', e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2059">
              <option value="PKR" data-id="element-2060">PKR - Pakistani Rupee</option>
              <option value="USD" data-id="element-2061">USD - US Dollar</option>
              <option value="EUR" data-id="element-2062">EUR - Euro</option>
              <option value="GBP" data-id="element-2063">GBP - British Pound</option>
            </select>
          </div>
          <div data-id="element-2064">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2065">
              Due Date
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-2066">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2067">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2068" />
              </div>
              <input type="date" value={rtpParams.dueDate} onChange={e => handleRTPParamChange('dueDate', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2069" />
            </div>
          </div>
          <div data-id="element-2070">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2071">
              Invoice Number
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-2072">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2073">
                <HashIcon className="h-5 w-5 text-gray-400" data-id="element-2074" />
              </div>
              <input type="text" placeholder="Invoice Number" value={rtpParams.invoiceNumber} onChange={e => handleRTPParamChange('invoiceNumber', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2075" />
            </div>
          </div>
          <div className="md:col-span-2" data-id="element-2076">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2077">
              Description
            </label>
            <textarea placeholder="Payment description" value={rtpParams.description} onChange={e => handleRTPParamChange('description', e.target.value)} rows={2} className="w-full pl-3 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2078" />
          </div>
          <div data-id="element-2079">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2080">
              Payment Method
            </label>
            <select value={rtpParams.paymentMethod} onChange={e => handleRTPParamChange('paymentMethod', e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2081">
              <option value="rtp" data-id="element-2082">RTP</option>
              <option value="qr" data-id="element-2083">QR Code</option>
              <option value="pos" data-id="element-2084">POS</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end" data-id="element-2085">
          <button onClick={handleGenerateRTPs} disabled={selectedMerchants.length === 0 || !rtpParams.amount || !rtpParams.customerAlias && !rtpParams.customerIBAN} className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${selectedMerchants.length === 0 || !rtpParams.amount || !rtpParams.customerAlias && !rtpParams.customerIBAN ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-2086">
            <CreditCardIcon className="h-4 w-4 mr-2" data-id="element-2087" />
            Generate RTP Requests ({selectedMerchants.length} selected)
          </button>
        </div>
      </Card>

      {/* Merchant List */}
      <Card data-id="element-2088">
        <div className="mb-4 flex items-center justify-between" data-id="element-2089">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2090">
            Showing {filteredMerchants.length} merchants
          </div>
          <div className="flex items-center" data-id="element-2091">
            <input type="checkbox" id="selectAll" checked={selectAll} onChange={handleSelectAll} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2092" />
            <label htmlFor="selectAll" className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2093">
              Select All
            </label>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-2094">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2095">
            <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-2096">
              <tr data-id="element-2097">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2098">
                  <span className="sr-only" data-id="element-2099">Select</span>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2100">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2101">
                  Merchant ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2102">
                  Store Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2103">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2104">
                  Pending RTPs
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2105">
                  Last RTP Generated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800" data-id="element-2106">
              {filteredMerchants.length === 0 ? <tr data-id="element-2107">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-2108">
                    No merchants found matching your criteria
                  </td>
                </tr> : filteredMerchants.map(merchant => <tr key={merchant.id} className={selectedMerchants.includes(merchant.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''} data-id="element-2109">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2110">
                      <input type="checkbox" checked={selectedMerchants.includes(merchant.id)} onChange={() => handleSelectMerchant(merchant.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2111" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2112">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2113">
                        {merchant.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2114">
                        {merchant.businessType}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2115">
                      {merchant.merchantId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2116">
                      {merchant.storeLocation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2117">
                      {getStatusBadge(merchant.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-2118">
                      {merchant.pendingRTPRequests > 0 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" data-id="element-2119">
                          {merchant.pendingRTPRequests}
                        </span> : <span className="text-gray-500 dark:text-gray-400" data-id="element-2120">
                          0
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2121">
                      {formatDate(merchant.lastRTPGenerated)}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Generation Modal */}
      <Modal isOpen={showGenerationModal} onClose={() => !isGenerating && setShowGenerationModal(false)} title="Generating RTP Requests" size="lg" data-id="element-2122">
        <div className="space-y-4" data-id="element-2123">
          {/* Progress Bar */}
          <div data-id="element-2124">
            <div className="flex justify-between mb-1" data-id="element-2125">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2126">
                Progress: {generationProgress}%
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2127">
                {generatedRTPs.filter(rtp => rtp.status !== 'pending').length}{' '}
                of {generatedRTPs.length} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-2128">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{
              width: `${generationProgress}%`
            }} data-id="element-2129"></div>
            </div>
          </div>

          {/* RTP Generation Status */}
          <div className="mt-4 max-h-96 overflow-y-auto" data-id="element-2130">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2131">
              <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-2132">
                <tr data-id="element-2133">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2134">
                    Merchant
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2135">
                    Merchant ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2136">
                    RTP Reference
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2137">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800" data-id="element-2138">
                {generatedRTPs.map((rtp, index) => <tr key={index} data-id="element-2139">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-2140">
                      {rtp.merchantName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2141">
                      {rtp.merchantId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2142">
                      {rtp.rtpReference || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2143">
                      {rtp.status === 'pending' && <span className="flex items-center" data-id="element-2144">
                          <RefreshCwIcon className="h-4 w-4 mr-1 text-yellow-500 animate-spin" data-id="element-2145" />
                          <span className="text-yellow-500" data-id="element-2146">Pending</span>
                        </span>}
                      {rtp.status === 'generated' && <span className="flex items-center" data-id="element-2147">
                          <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" data-id="element-2148" />
                          <span className="text-green-500" data-id="element-2149">Generated</span>
                        </span>}
                      {rtp.status === 'failed' && <span className="flex items-center" data-id="element-2150">
                          <XIcon className="h-4 w-4 mr-1 text-red-500" data-id="element-2151" />
                          <span className="text-red-500" data-id="element-2152">Failed</span>
                          <span className="ml-2 text-xs text-gray-500" data-id="element-2153">
                            {rtp.errorMessage}
                          </span>
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2154">
            {isGenerating ? <button disabled className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed" data-id="element-2155">
                <RefreshCwIcon className="h-4 w-4 mr-2 inline animate-spin" data-id="element-2156" />
                Generating...
              </button> : <>
                <button onClick={() => setShowGenerationModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2157">
                  Close
                </button>
                <button onClick={() => setShowDownloadModal(true)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2158">
                  <DownloadIcon className="h-4 w-4 mr-2 inline" data-id="element-2159" />
                  Download RTP List
                </button>
              </>}
          </div>
        </div>
      </Modal>

      {/* Download Options Modal */}
      <Modal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} title="Download RTP List" size="md" data-id="element-2160">
        <div className="space-y-4" data-id="element-2161">
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2162">
            Choose a download format for your RTP list:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2163">
            <button onClick={() => handleDownloadRTPs('csv')} className="p-4 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 flex flex-col items-center" data-id="element-2164">
              <FileIcon className="h-10 w-10 text-blue-500 mb-2" data-id="element-2165" />
              <span className="text-sm font-medium" data-id="element-2166">CSV Format</span>
              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2167">
                Download RTP list as CSV
              </span>
            </button>
            <button onClick={() => handleDownloadRTPs('xlsx')} className="p-4 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 flex flex-col items-center" data-id="element-2168">
              <FileTextIcon className="h-10 w-10 text-green-500 mb-2" data-id="element-2169" />
              <span className="text-sm font-medium" data-id="element-2170">XLSX Format</span>
              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2171">
                Download RTP list as Excel
              </span>
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2172">
            <button onClick={() => setShowDownloadModal(false)} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2173">
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} title="Bulk RTP Generation History" size="lg" data-id="element-2174">
        <div className="space-y-4" data-id="element-2175">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-2176">
            Review your previous bulk RTP generation operations:
          </p>
          <div className="overflow-x-auto" data-id="element-2177">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2178">
              <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-2179">
                <tr data-id="element-2180">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2181">
                    Date & Time
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2182">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2183">
                    Merchants
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2184">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2185">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800" data-id="element-2186">
                {bulkHistory.map(item => <tr key={item.id} data-id="element-2187">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2188">
                      {new Date(item.date).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2189">
                      {item.user}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2190">
                      {item.merchantCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2191">
                      <StatusBadge status={item.status === 'completed' ? 'success' : 'warning'} text={item.status.charAt(0).toUpperCase() + item.status.slice(1)} data-id="element-2192" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-2193">
                      {item.downloadAvailable ? <button className="text-blue-600 dark:text-blue-400 hover:underline" data-id="element-2194">
                          <DownloadIcon className="h-4 w-4 inline mr-1" data-id="element-2195" />
                          Download
                        </button> : <span className="text-gray-400 dark:text-gray-500" data-id="element-2196">
                          Not available
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end" data-id="element-2197">
            <button onClick={() => setShowHistoryModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2198">
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>;
};
export default BulkRTP;